import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['documentsList', 'placementsField', 'dropzone']
  static values = {
    stagedDocumentsUrl: String,
    attachedDocumentsUrl: { type: String, default: '' },
  }

  connect() {
    this.placementsByDocument = new Map()

    this.boundOnPlacementsChanged = (event) => {
      this.onPlacementsChanged(event)
    }
    document.addEventListener(
      'document-placer:placements-changed',
      this.boundOnPlacementsChanged
    )

    if (this.hasDropzoneTarget) {
      this.boundOnDragOver = (event) => this.onDragOver(event)
      this.boundOnDragLeave = (event) => this.onDragLeave(event)
      this.boundOnDrop = (event) => this.onDrop(event)
      this.dropzoneTarget.addEventListener('dragover', this.boundOnDragOver)
      this.dropzoneTarget.addEventListener('dragleave', this.boundOnDragLeave)
      this.dropzoneTarget.addEventListener('drop', this.boundOnDrop)
    }

    this.boundOnWindowDragOver = (event) => this.onWindowDragOver(event)
    this.boundOnWindowDrop = (event) => this.onWindowDrop(event)
    window.addEventListener('dragover', this.boundOnWindowDragOver)
    window.addEventListener('drop', this.boundOnWindowDrop)

    this.boundOnModeChange = (event) => this.onModeChange(event)
    this.element.addEventListener('change', this.boundOnModeChange)
    this.broadcastMode()
  }

  disconnect() {
    document.removeEventListener(
      'document-placer:placements-changed',
      this.boundOnPlacementsChanged
    )

    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.removeEventListener(
        'dragover',
        this.boundOnDragOver
      )
      this.dropzoneTarget.removeEventListener(
        'dragleave',
        this.boundOnDragLeave
      )
      this.dropzoneTarget.removeEventListener('drop', this.boundOnDrop)
    }

    window.removeEventListener('dragover', this.boundOnWindowDragOver)
    window.removeEventListener('drop', this.boundOnWindowDrop)

    this.element.removeEventListener('change', this.boundOnModeChange)
  }

  onModeChange(event) {
    if (event.target?.name !== 'signature_request[mode]') return
    this.broadcastMode()
  }

  broadcastMode() {
    const checked = this.element.querySelector(
      'input[name="signature_request[mode]"]:checked'
    )
    const mode = checked ? checked.value : 'parallel'
    const dispatched = new CustomEvent(
      'signature-session-form:mode-changed',
      { detail: { mode }, bubbles: true }
    )
    document.dispatchEvent(dispatched)
  }

  onWindowDragOver(event) {
    if (this.containsFiles(event)) event.preventDefault()
  }

  onWindowDrop(event) {
    if (this.containsFiles(event)) event.preventDefault()
  }

  containsFiles(event) {
    const types = Array.from(event.dataTransfer?.types || [])
    return types.includes('Files')
  }

  onDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    this.dropzoneTarget.classList.add('border-primary', 'bg-primary/5')
  }

  onDragLeave(event) {
    if (event.target === this.dropzoneTarget) {
      this.dropzoneTarget.classList.remove('border-primary', 'bg-primary/5')
    }
  }

  async onDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove('border-primary', 'bg-primary/5')

    const files = Array.from(event.dataTransfer?.files || [])
      .filter((file) => file.type === 'application/pdf')
    if (files.length === 0) return

    await this.handleFiles(files)
  }

  async uploadFiles(event) {
    const input = event.target
    const files = Array.from(input.files || [])
    input.value = ''
    if (files.length === 0) return

    await this.handleFiles(files)
  }

  async handleFiles(files) {
    let lastRedirect = null
    for (const file of files) {
      try {
        const document = await this.uploadOne(file)
        const attached = await this.attachToDraft(document.id)
        if (attached?.redirect_url) {
          lastRedirect = attached.redirect_url
        } else {
          this.appendDocument(document)
        }
      } catch (error) {
        console.error('Upload failed', error)
      }
    }

    if (lastRedirect) {
      window.location.href = lastRedirect
    }
  }

  async attachToDraft(documentId) {
    if (!this.hasAttachedDocumentsUrlValue ||
        this.attachedDocumentsUrlValue.length === 0) {
      return null
    }

    const formData = new FormData()
    formData.append('document_id', documentId)

    const response = await fetch(this.attachedDocumentsUrlValue, {
      method: 'POST',
      body: formData,
      headers: { 'X-CSRF-Token': this.csrfToken() },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || 'Attach error')
    }

    return response.json()
  }

  async uploadOne(file) {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(this.stagedDocumentsUrlValue, {
      method: 'POST',
      body: formData,
      headers: { 'X-CSRF-Token': this.csrfToken() },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.error || 'Upload error')
    }

    return response.json()
  }

  appendDocument(documentPayload) {
    const item = window.document.createElement('li')
    item.className = 'flex items-center justify-between text-sm gap-2'
    item.dataset.stagedDocumentId = String(documentPayload.id)

    const label = window.document.createElement('span')
    label.className = 'text-gray-700 truncate'
    label.textContent =
      documentPayload.filename || `Document #${documentPayload.id}`

    const hidden = window.document.createElement('input')
    hidden.type = 'hidden'
    hidden.name = 'signature_request[document_ids][]'
    hidden.value = documentPayload.id

    const removeButton = window.document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = 'text-xs text-gray-500 hover:text-red-600'
    removeButton.textContent = '×'
    removeButton.addEventListener('click', () => {
      item.remove()
    })

    item.appendChild(label)
    item.appendChild(hidden)
    item.appendChild(removeButton)
    this.documentsListTarget.appendChild(item)
  }

  onPlacementsChanged(event) {
    if (!this.hasPlacementsFieldTarget) return

    const { documentId, placements } = event.detail || {}
    if (documentId === undefined) return

    this.placementsByDocument.set(Number(documentId), placements || [])
    this.syncPlacementsField()
  }

  syncPlacementsField() {
    const all = []
    this.placementsByDocument.forEach((placements) => {
      placements.forEach((placement) => all.push(placement))
    })
    this.placementsFieldTarget.value = JSON.stringify(all)
  }

  csrfToken() {
    const tag = window.document.querySelector('meta[name="csrf-token"]')
    return tag ? tag.content : ''
  }
}
